import { config } from '../config';

export interface CommentAnchor {
  selector: string;
  xRatio: number;
  yRatio: number;
}

export interface CommentPosition {
  xPct: number;
  yPct: number;
  anchor?: CommentAnchor;
}

export type CommentUpdate = Partial<CommentPosition> & { text?: string; resolved?: boolean };

export interface Comment {
  commentId: string;
  prototypeSlug: string;
  authorEmail: string;
  authorName: string;
  text: string;
  createdAt: string;
  updatedAt?: string;
  xPct?: number;
  yPct?: number;
  parentCommentId?: string;
  anchor?: CommentAnchor;
  resolved?: boolean;
}

export interface CommentStats {
  open: number;
  resolved: number;
}

export type CommentStatsByScope = Record<string, CommentStats>;

/** Counts root discussions across the prototype and all its subviews. */
export function getPrototypeCommentStats(stats: CommentStatsByScope, slug: string): CommentStats {
  return Object.entries(stats).reduce((total, [scope, counts]) => {
    if (scope === slug || scope.startsWith(`${slug}--`)) {
      total.open += counts.open;
      total.resolved += counts.resolved;
    }
    return total;
  }, { open: 0, resolved: 0 });
}

// ── localStorage fallback (dev local sans BFF) ──────────────────────────────

const LS_KEY = (slug: string) => `nemo_comments_${slug}`;

function lsGet(slug: string): Comment[] {
  try { return JSON.parse(localStorage.getItem(LS_KEY(slug)) ?? '[]') as Comment[]; }
  catch { return []; }
}

function lsSet(slug: string, comments: Comment[]): void {
  localStorage.setItem(LS_KEY(slug), JSON.stringify(comments));
}

export const LOCAL_DEV_EMAIL = 'local@dev';
export const LOCAL_DEV_NAME = 'Moi (local)';

// ── BFF helpers ──────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
  const { oktaAuth } = await import('../auth/OktaProvider');
  if (!oktaAuth) throw new Error('Okta not configured');
  const token = await oktaAuth.getOrRenewAccessToken();
  if (!token) throw new Error('No Okta access token');
  return token;
}

function commentsUrl(slug: string, commentId?: string): string {
  const base = `${config.bffUrl}/prototyper/comments/${encodeURIComponent(slug)}`;
  return commentId ? `${base}/${encodeURIComponent(commentId)}` : base;
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Returns discussion counts per storage scope, without comment text or authors. */
export async function fetchCommentStats(): Promise<CommentStatsByScope> {
  if (!config.bffUrl) {
    const stats: CommentStatsByScope = {};
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith('nemo_comments_')) continue;
      const slug = key.slice('nemo_comments_'.length);
      const comments = JSON.parse(localStorage.getItem(key) ?? '[]') as Comment[];
      stats[slug] = comments.reduce((counts, comment) => {
        if (!comment.parentCommentId) counts[comment.resolved ? 'resolved' : 'open'] += 1;
        return counts;
      }, { open: 0, resolved: 0 });
    }
    return stats;
  }
  const token = await getToken();
  const res = await fetch(`${config.bffUrl}/prototyper/comment-stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`BFF GET /prototyper/comment-stats ${res.status}`);
  return res.json() as Promise<CommentStatsByScope>;
}

export async function fetchComments(slug: string): Promise<Comment[]> {
  if (!config.bffUrl) return lsGet(slug);
  const token = await getToken();
  const res = await fetch(commentsUrl(slug), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`BFF GET /prototyper/comments/${slug} ${res.status}`);
  return res.json() as Promise<Comment[]>;
}

export async function postComment(
  slug: string,
  text: string,
  opts?: Partial<CommentPosition> & { parentCommentId?: string; authorName?: string },
): Promise<{ commentId: string }> {
  if (!config.bffUrl) {
    const comments = lsGet(slug);
    const newComment: Comment = {
      commentId: `local-${crypto.randomUUID()}`,
      prototypeSlug: slug,
      authorEmail: LOCAL_DEV_EMAIL,
      authorName: LOCAL_DEV_NAME,
      text,
      createdAt: new Date().toISOString(),
      ...opts,
    };
    lsSet(slug, [...comments, newComment]);
    return { commentId: newComment.commentId };
  }
  const token = await getToken();
  const { authorName, ...rest } = opts ?? {};
  const res = await fetch(commentsUrl(slug), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, ...(authorName ? { authorName } : {}), ...rest }),
  });
  if (!res.ok) throw new Error(`BFF POST /prototyper/comments/${slug} ${res.status}`);
  return res.json() as Promise<{ commentId: string }>;
}

export async function deleteComment(slug: string, commentId: string): Promise<void> {
  if (!config.bffUrl) {
    lsSet(slug, lsGet(slug).filter((c) => c.commentId !== commentId && c.parentCommentId !== commentId));
    return;
  }
  const token = await getToken();
  const res = await fetch(commentsUrl(slug, commentId), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`BFF DELETE /prototyper/comments/${slug}/${commentId} ${res.status}`);
}

export async function editComment(slug: string, commentId: string, text: string): Promise<void> {
  return updateComment(slug, commentId, { text });
}

export async function updateComment(slug: string, commentId: string, changes: CommentUpdate): Promise<void> {
  if (!config.bffUrl) {
    const comments = lsGet(slug);
    const idx = comments.findIndex((c) => c.commentId === commentId);
    if (idx === -1) throw new Error('Comment not found');
    comments[idx] = { ...comments[idx], ...changes, updatedAt: new Date().toISOString() };
    lsSet(slug, comments);
    return;
  }
  const token = await getToken();
  const res = await fetch(commentsUrl(slug, commentId), {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!res.ok) throw new Error(`BFF PATCH /prototyper/comments/${slug}/${commentId} ${res.status}`);
}
